import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];
  #megaCloseTimer;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.addEventListener('mouseover', this.#onMegaMouseOver);
    this.addEventListener('mouseout', this.#onMegaMouseOut);
    this.addEventListener('focusin', this.#onMegaFocusIn);
    this.addEventListener('focusout', this.#onMegaFocusOut);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.removeEventListener('mouseover', this.#onMegaMouseOver);
    this.removeEventListener('mouseout', this.#onMegaMouseOut);
    this.removeEventListener('focusin', this.#onMegaFocusIn);
    this.removeEventListener('focusout', this.#onMegaFocusOut);
    this.#clearMegaCloseTimer();
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * Open Shokz mega drawer panels on desktop hover without changing mobile tap behavior.
   * @param {MouseEvent} event
   */
  #onMegaMouseOver = (event) => {
    if (!isDesktopDrawer()) return;

    const item = getMegaListItem(event.target);
    if (!item || !this.contains(item)) return;
    if (event.relatedTarget instanceof Node && item.contains(event.relatedTarget)) return;

    this.#clearMegaCloseTimer();
    this.#openMegaItem(item);
  };

  /**
   * Close Shokz mega drawer panels after the pointer leaves the trigger and flyout.
   * @param {MouseEvent} event
   */
  #onMegaMouseOut = (event) => {
    if (!isDesktopDrawer()) return;

    const item = getMegaListItem(event.target);
    if (!item || !this.contains(item)) return;
    if (event.relatedTarget instanceof Node && item.contains(event.relatedTarget)) return;

    const details = getMegaDetails(item);
    if (!details || details.matches(':focus-within')) return;

    this.#scheduleMegaClose(details);
  };

  /**
   * Open Shokz mega drawer panels for keyboard users on desktop.
   * @param {FocusEvent} event
   */
  #onMegaFocusIn = (event) => {
    if (!isDesktopDrawer()) return;

    const item = getMegaListItem(event.target);
    if (!item || !this.contains(item)) return;

    this.#clearMegaCloseTimer();
    this.#openMegaItem(item);
  };

  /**
   * Close Shokz mega drawer panels once keyboard focus leaves the trigger and flyout.
   * @param {FocusEvent} event
   */
  #onMegaFocusOut = (event) => {
    if (!isDesktopDrawer()) return;

    const item = getMegaListItem(event.target);
    if (!item || !this.contains(item)) return;
    if (event.relatedTarget instanceof Node && item.contains(event.relatedTarget)) return;

    const details = getMegaDetails(item);
    if (!details) return;

    this.#scheduleMegaClose(details);
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * @param {Element} item
   */
  #openMegaItem(item) {
    const details = getMegaDetails(item);
    const summary = details?.querySelector('summary');

    if (!details || !summary) return;

    details.setAttribute('open', '');
    details.classList.add('menu-open');
    summary.setAttribute('aria-expanded', 'true');
  }

  /**
   * @param {HTMLDetailsElement} details
   */
  #scheduleMegaClose(details) {
    this.#clearMegaCloseTimer();

    this.#megaCloseTimer = setTimeout(() => {
      if (details.matches(':hover, :focus-within')) return;

      reset(details);
    }, 180);
  }

  #clearMegaCloseTimer() {
    if (!this.#megaCloseTimer) return;

    clearTimeout(this.#megaCloseTimer);
    this.#megaCloseTimer = undefined;
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}

/**
 * @returns {boolean}
 */
function isDesktopDrawer() {
  return window.matchMedia('(min-width: 750px)').matches;
}

/**
 * @param {EventTarget | null} target
 * @returns {Element | null}
 */
function getMegaListItem(target) {
  if (!(target instanceof Element)) return null;

  return target.closest('.menu-drawer__list-item--has-mega');
}

/**
 * @param {Element} item
 * @returns {HTMLDetailsElement | null}
 */
function getMegaDetails(item) {
  const details = item.querySelector(':scope > details');

  return details instanceof HTMLDetailsElement ? details : null;
}
